import * as d3 from 'd3';

export let BarChart = function(_parentElement, _data) {
	this.parentElement = _parentElement;
	this.data = _data
	this.textFormats = Object.freeze({"number": 1, "percent": 2, "dollars": 3});
	this.textFormat = this.textFormats.percent;
	this.filteredData = this.data[0];
	this.totalClaims = 1;
	this.avgCost = 69680;
	this.init();
}

BarChart.prototype.init = function() {
	let chart = this;
	chart.margin = {top: 25, right: 300, bottom: 100, left: 300};
	chart.width = 1000 - chart.margin.left - chart.margin.right;
	chart.height = 650 - chart.margin.top - chart.margin.bottom;

	chart.svg = d3.select("#" + chart.parentElement).append("svg")
		.attr("width", chart.width + chart.margin.left + chart.margin.right)
		.attr("height", chart.height + chart.margin.top + chart.margin.bottom)
		.append("g")
		.attr("transform", `translate(${chart.margin.left}, ${chart.margin.top})`);

	chart.x = d3.scaleBand()
		.paddingInner(0.2)
		.paddingOuter(0.1)
		.domain(["gai", "org"])
		.range([0, chart.width]);

	chart.colors = d3.scaleOrdinal()
		.range(["#a0d3f2", "#d9d9d9"])
	chart.colors.domain = chart.x.domain

	chart.y = d3.scaleLinear()
		.domain([0, chart.data.length-1])
		.rangeRound([chart.height, 0])
		.clamp(true);


	let xAxisLabels = {"gai": "GradientAI", "org": "Organization"}
	chart.xAxis = d3.axisBottom()
		.scale(chart.x)
		.tickFormat(s => xAxisLabels[s]);

	chart.yAxis = d3.axisLeft()
		.scale(chart.y);

	// x axis element
	chart.svg.append("g")
		.attr("class", "x-axis axis")
		.attr("transform", "translate(0," + chart.height + ")")
		.call(chart.xAxis);

	// y axis element
	chart.svg.append("g")
		.attr("class", "y-axis axis")
		.call(chart.yAxis);

	// tracker
	chart.tracker = chart.svg.append("g")
		.attr("id", "tracker")
		.call(d3.drag().on("drag", function(event) {
			let newClaims = Math.round(chart.y.invert(event.y));
			chart.update(newClaims);
		}));

	chart.trackerLine = chart.tracker.append("line")
		.attr("id", "tracker-line")
		.attr("x1", -chart.margin.left)
		.attr("x2", 0);

	chart.trackerText = chart.tracker.append("text")
		.attr("id", "tracker-text")
		.attr("x", -chart.margin.left)
		.text("Total claims: " + chart.totalClaims);

	chart.differenceLine = chart.svg.append("path")
		.attr("id", "difference-line")
		.attr("stroke", "black")

	chart.differenceText = chart.svg.append("text")
		.attr("id", "difference-text")
    .attr("class", "green")
		.attr("x", chart.width+10)
};

BarChart.prototype.update = function(t) {
	let chart = this;
	chart.filteredData = chart.data[t];
	chart.totalClaims = t+1;

	chart.updateText();
	chart.trackerLine
		.attr('y1', chart.y(chart.totalClaims))
		.attr('y2', chart.y(chart.totalClaims));
	chart.trackerText
		.attr("y", chart.y(chart.totalClaims) - 5)
		.text("Top claims");
	chart.differenceLine
		.attr("d", "M " + (chart.width) + " " + Math.max(chart.y(chart.filteredData.data[0].value), chart.y(chart.filteredData.data[1].value)) + " " +
				   "h 10 " +
				   "m -5 0 " +
				   "V " + Math.min(chart.y(chart.filteredData.data[0].value), chart.y(chart.filteredData.data[1].value)) + " " +
				   "m -5 0" +
				   "h 10");

	let bars = chart.svg.selectAll(".bar")
		.data(chart.filteredData.data, d => d.model)

	bars.enter().append("rect")
		.attr("class", "bar")

		.merge(bars)
		.attr("width", chart.x.bandwidth())
		.attr("height", d => chart.height - chart.y(d.value))
		.attr("x", d => chart.x(d.model))
		.attr("y", d => chart.y(d.value))
		.attr("fill", d => chart.colors(d.model));
	bars.exit().remove();
}

BarChart.prototype.updateText = function() {
	let chart = this;

	let barTexts = chart.svg.selectAll(".bar-text")
		.data(chart.filteredData.data, d => d.model);
	barTexts.enter().append("text")
		.attr("class", "bar-text")

		.merge(barTexts)
		.attr("x", d => chart.x(d.model) + chart.x.bandwidth()/2)
		.attr("y", d => chart.y(d.value) - 5)
		.text(d => chart.getBarText(d.value));
	barTexts.exit().remove();

	// split the difference between the heights of the bars
	chart.differenceText
		.attr("y", () => Math.max(chart.y(chart.filteredData.data[0].value), chart.y(chart.filteredData.data[1].value)) - (Math.abs(chart.y(chart.filteredData.data[0].value) - chart.y(chart.filteredData.data[1].value))/2) + 6)
		.text(chart.getDifferenceText())
}


BarChart.prototype.getBarText = function(value) {
	let chart = this;
	if (chart.textFormat === chart.textFormats.percent) {
		return (Math.round(value/chart.data.length*1000)/10).toFixed(1) + "%";
	}
	else if (chart.textFormat === chart.textFormats.dollars) {
		return "$" + (value*chart.avgCost).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
	}
	return value + " claims";

}


BarChart.prototype.setTextFormat = function(option) {
	let chart = this;
	try {
		chart.textFormat = chart.textFormats[option];
		chart.updateText();
	}
	catch {
		console.log("Unrecognized text format: " + option);
	}
}

BarChart.prototype.getDifferenceText = function() {
	let chart = this;
	if (chart.textFormat === chart.textFormats.percent) {
		return (Math.round((chart.filteredData.data[0].value - chart.filteredData.data[1].value)/chart.filteredData.data[1].value*1000)/10).toFixed(1) + "%";
	}
	else if (chart.textFormat === chart.textFormats.dollars) {
		return "$" + ((chart.filteredData.data[0].value - chart.filteredData.data[1].value)*chart.avgCost).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
	}
	return (chart.filteredData.data[0].value - chart.filteredData.data[1].value) + " claims";
}
